import React, { useState, useEffect, useCallback } from 'react';
import styles from '../MarketDataImport/MarketDataImport.module.css';
import Card from '../../components/common/Card/Card';
import Button from '../../components/common/Button/Button';
import Spinner from '../../components/common/Spinner/Spinner';
import api from '../../services/api';

const GrossDeliverables = () => {
  const [file, setFile] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/delivery_import_data/logs');
      setLogs(response?.data || []);
    } catch (err) {
      console.error('Failed to fetch logs:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setError(null);
    setSuccess(null);
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      
      const formData = new FormData();
      formData.append('deliveryFile', file);

      await api.post('/delivery_import_data/upload', formData);
      setSuccess('File uploaded successfully. Processing started in background.');
      setFile(null);
      document.getElementById('market-file-input').value = '';
      fetchLogs();
      
    } catch (err) {
      setError(err.message || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'completed': return styles.statusCompleted;
      case 'processing': return styles.statusProcessing;
      case 'failed': return styles.statusFailed;
      default: return styles.statusPending;
    }
  };

  const filteredLogs = logs.filter(log => {
    const matchesSearch = log.filename.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || log.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const sortedLogs = [...filteredLogs].sort((a, b) => {
    if (a[sortConfig.key] < b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (a[sortConfig.key] > b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const totalPages = Math.ceil(sortedLogs.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedLogs.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return 'unfold_more';
    return sortConfig.direction === 'asc' ? 'arrow_upward' : 'arrow_downward';
  };

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <h2>Gross Deliverables</h2>
        <p>Upload Gross Deliverables files to update market data.</p>
      </header>

      <div className={styles.grid}>
        <Card className={styles.uploadCard}>
          <h3>Upload New File</h3>
          <div className={styles.uploadForm}>
            <input 
              type="file" 
              id="market-file-input"
              accept=".csv,.xlsx,.xls" 
              onChange={handleFileChange}
              className={styles.fileInput}
            />
            <div className={styles.helpText}>
              Supported formats: .csv, .xlsx, .xls
            </div>
            {error && <div className={styles.errorMsg}>{error}</div>}
            {success && <div className={styles.successMsg}>{success}</div>}
            
            <Button 
              onClick={handleUpload} 
              disabled={!file || uploading}
              className={styles.uploadBtn}
            >
              {uploading ? <><Spinner size="small" /> Uploading...</> : 'Start Import Process'}
            </Button>
          </div>
        </Card>

        <Card className={styles.logsCard}>
          <div className={styles.cardHeader}>
            <div className={styles.headerLeft}>
              <h3>Import History & Status</h3>
              <div className={styles.filters}>
                <div className={styles.searchWrapper}>
                  <span className="material-symbols-outlined">search</span>
                  <input 
                    type="text" 
                    placeholder="Search by filename..." 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className={styles.searchInput}
                  />
                </div>
                <select 
                  value={statusFilter} 
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className={styles.statusSelect}
                >
                  <option value="all">All Status</option>
                  <option value="completed">Completed</option>
                  <option value="processing">Processing</option>
                  <option value="failed">Failed</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
            </div>
            <Button onClick={fetchLogs} variant="secondary" size="small">Refresh</Button>
          </div>
          
          <div className={styles.tableWrapper}>
            {loading && logs.length === 0 ? (
              <div className={styles.loadingState}><Spinner /> Loading logs...</div>
            ) : (
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th onClick={() => requestSort('created_at')} className={styles.sortableHeader}>
                      Date <span className="material-symbols-outlined">{getSortIcon('created_at')}</span>
                    </th>
                    <th onClick={() => requestSort('filename')} className={styles.sortableHeader}>
                      Filename <span className="material-symbols-outlined">{getSortIcon('filename')}</span>
                    </th>
                    <th>Status</th>
                    <th>Stats (I/U/F)</th>
                    <th>Progress</th>
                  </tr>
                </thead>
                  <tbody>
                    {currentItems.length === 0 ? (
                      <tr><td colSpan="5" className={styles.noData}>No import logs found matching criteria</td></tr>
                    ) : (
                      currentItems.map(log => (
                        <tr key={log.id}>
                          <td>{new Date(log.created_at).toLocaleString()}</td>
                          <td className={styles.filenameCell} title={log.filename}>{log.filename}</td>
                          <td>
                            <span className={`${styles.statusBadge} ${getStatusClass(log.status)}`}>
                              {log.status}
                            </span>
                          </td>
                          <td>
                            <span className={styles.statIns}>{log.inserted_records}</span> / 
                            <span className={styles.statUpd}>{log.updated_records}</span> / 
                            <span className={styles.statFail}>{log.failed_records}</span>
                          </td>
                          <td>
                            <div className={styles.progressContainer}>
                              <div 
                                className={styles.progressBar} 
                                style={{ width: `${log.total_records > 0 ? (log.processed_records / log.total_records) * 100 : 0}%` }}
                              ></div>
                              <span className={styles.progressText}>
                                {log.processed_records} / {log.total_records}
                              </span>
                            </div>
                            {log.error_message && <div className={styles.logError}>{log.error_message}</div>}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}
            </div>

            {sortedLogs.length > itemsPerPage && (
              <div className={styles.pagination}>
                <button 
                  onClick={() => paginate(currentPage - 1)} 
                  disabled={currentPage === 1}
                  className={styles.pageBtn}
                >
                  <span className="material-symbols-outlined">chevron_left</span>
                </button>
                
                {[...Array(totalPages)].map((_, index) => {
                  const pageNum = index + 1;
                  if (
                    pageNum === 1 || 
                    pageNum === totalPages || 
                    (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={pageNum}
                        onClick={() => paginate(pageNum)}
                        className={`${styles.pageBtn} ${currentPage === pageNum ? styles.pageActive : ''}`}
                      >
                        {pageNum}
                      </button>
                    );
                  } else if (
                    pageNum === currentPage - 2 || 
                    pageNum === currentPage + 2
                  ) {
                    return <span key={pageNum} className={styles.pageDots}>...</span>;
                  }
                  return null;
                })}

                <button 
                  onClick={() => paginate(currentPage + 1)} 
                  disabled={currentPage === totalPages}
                  className={styles.pageBtn}
                >
                  <span className="material-symbols-outlined">chevron_right</span>
                </button>
              </div>
            )}
          </Card>
      </div>
    </div>
  );
};

export default GrossDeliverables;
